import { FormEvent } from 'react';
import { Bell, AlertCircle } from 'lucide-react';
import { Appointment, UserAccount } from '../../types/api';
import { formatDateTime, getCurrentDateTime } from '../../lib/formatters';
import {
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  FormCheckbox,
  Button,
  Alert,
} from '../ui';
import ReminderMinutesInput from './ReminderMinutesInput';

interface AppointmentFormProps {
  appointment?: Appointment | null;
  userAccounts?: UserAccount[];
  selectedUsers: number[];
  onSelectedUsersChange: (users: number[]) => void;
  reminderMinutes: number[];
  onReminderMinutesChange: (minutes: number[]) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onClose: () => void;
  isLoading: boolean;
}

const editableStatus = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  canceled: 'Cancelado',
  completed: 'Concluído',
} as const;

function AppointmentForm({
  appointment,
  userAccounts,
  selectedUsers,
  onSelectedUsersChange,
  reminderMinutes,
  onReminderMinutesChange,
  onSubmit,
  onClose,
  isLoading,
}: AppointmentFormProps) {
  const isReadOnly = appointment?.status === 'canceled' || appointment?.status === 'completed';
  const currentDateTime = getCurrentDateTime();

  return (
    <Form onSubmit={onSubmit}>
      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="title" required>
            Título
          </FormLabel>
          <FormInput
            id="title"
            name="title"
            defaultValue={appointment?.title}
            required
            disabled={isReadOnly}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="location">Local</FormLabel>
          <FormInput
            id="location"
            name="location"
            defaultValue={appointment?.location}
            disabled={isReadOnly}
          />
        </FormField>
      </div>

      <FormField>
        <FormLabel htmlFor="description">Descrição</FormLabel>
        <FormInput
          id="description"
          name="description"
          defaultValue={appointment?.description}
          disabled={isReadOnly}
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="start_time" required>
            Data/Hora Início
          </FormLabel>
          <FormInput
            type="datetime-local"
            id="start_time"
            name="start_time"
            defaultValue={
              appointment?.start_time
                ? formatDateTime(appointment.start_time, 'iso')
                : currentDateTime
            }
            required
            disabled={isReadOnly}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="end_time">Data/Hora Fim</FormLabel>
          <FormInput
            type="datetime-local"
            id="end_time"
            name="end_time"
            defaultValue={
              appointment?.end_time ? formatDateTime(appointment.end_time, 'iso') : undefined
            }
            disabled={isReadOnly}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="recurrence_type">Tipo de Recorrência</FormLabel>
          <FormSelect
            id="recurrence_type"
            name="recurrence_type"
            defaultValue={appointment?.recurrence_type || 'none'}
            disabled={isReadOnly}
          >
            <option value="none">Sem recorrência</option>
            <option value="daily">Diário</option>
            <option value="weekly">Semanal</option>
            <option value="monthly">Mensal</option>
            <option value="yearly">Anual</option>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="status">Status</FormLabel>
          <FormSelect
            id="status"
            name="status"
            defaultValue={appointment?.status || 'pending'}
            disabled={isReadOnly}
          >
            {Object.entries(editableStatus).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </FormSelect>
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <div className="flex items-center h-full pt-6">
            <FormCheckbox
              id="all_day"
              name="all_day"
              value="true"
              defaultChecked={appointment?.all_day}
              disabled={isReadOnly}
            />
            <FormLabel htmlFor="all_day" className="ml-2 mb-0">
              Dia inteiro
            </FormLabel>
          </div>
        </FormField>

        <FormField>
          <FormLabel>
            <Bell className="h-4 w-4 inline-block mr-1" />
            Lembretes (minutos antes)
          </FormLabel>
          <ReminderMinutesInput
            value={reminderMinutes}
            onChange={onReminderMinutesChange}
            disabled={isReadOnly}
          />
        </FormField>
      </div>

      <FormField>
        <FormLabel>
          <Bell className="h-4 w-4 inline-block mr-1" />
          Notificar Usuários <span className="text-red-500">*</span>
        </FormLabel>
        <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto border rounded-md p-2">
          {userAccounts?.map(account => (
            <div key={account.id} className="flex items-center">
              <FormCheckbox
                id={`notify-${account.id}`}
                checked={selectedUsers.includes(account.id)}
                onChange={e => {
                  if (e.target.checked) {
                    onSelectedUsersChange([...selectedUsers, account.id]);
                  } else {
                    onSelectedUsersChange(selectedUsers.filter(id => id !== account.id));
                  }
                }}
                disabled={isReadOnly}
              />
              <FormLabel htmlFor={`notify-${account.id}`} className="ml-2 mb-0 truncate">
                {account.name}
              </FormLabel>
            </div>
          ))}
        </div>
      </FormField>

      {isReadOnly ? (
        <Alert variant="warning" icon={AlertCircle} className="mt-4">
          <h3 className="text-sm font-medium text-yellow-800">
            Compromisso {appointment?.status === 'canceled' ? 'Cancelado' : 'Concluído'}
          </h3>
          <p className="mt-2 text-sm text-yellow-700">
            Este compromisso está {appointment?.status === 'canceled' ? 'cancelado' : 'concluído'} e
            não pode ser editado. Por favor, crie um novo compromisso se necessário.
          </p>
        </Alert>
      ) : (
        <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
          <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
            {appointment ? 'Salvar' : 'Criar'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
            Cancelar
          </Button>
        </div>
      )}
    </Form>
  );
}

export default AppointmentForm;
